import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/link.default.js';

class RunLink extends skinResolutionMixin(RunElement) {
  #href = '';
  #target = '_self';
  #rel = '';
  #disabled = false;
  #download = '';

  static defaultSkin = defaultSkin;
  static skinLoaders = {
    wireframe: () => import('./skins/link.wireframe.js'),
  };

  static get observedAttributes() {
    return ['href', 'target', 'rel', 'disabled', 'download'];
  }

  _onClickBound = (e) => this.#handleClick(e);
  _onKeydownBound = (e) => this.#handleKeydown(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'link');
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('keydown', this._onKeydownBound);
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('keydown', this._onKeydownBound);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'href':     this.href = newValue !== null ? newValue : ''; break;
      case 'target':   this.target = newValue !== null ? newValue : '_self'; break;
      case 'rel':      this.rel = newValue !== null ? newValue : ''; break;
      case 'disabled': this.disabled = newValue !== null; break;
      case 'download': this.download = newValue !== null ? newValue : ''; break;
    }
  }

  get href() { return this.#href; }
  set href(value) {
    const coerced = String(value ?? '').trim();
    if (this.#href === coerced) return;
    this.#href = coerced;
    if (coerced) {
      this.setAttribute('href', coerced);
    } else {
      this.removeAttribute('href');
    }
    this.requestUpdate();
  }

  get target() { return this.#target; }
  set target(value) {
    const coerced = String(value ?? '').trim() || '_self';
    if (this.#target === coerced) return;
    this.#target = coerced;
    this.setAttribute('target', coerced);
    this.requestUpdate();
  }

  get rel() { return this.#rel; }
  set rel(value) {
    const coerced = String(value ?? '').trim();
    if (this.#rel === coerced) return;
    this.#rel = coerced;
    if (coerced) {
      this.setAttribute('rel', coerced);
    } else {
      this.removeAttribute('rel');
    }
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get download() { return this.#download; }
  set download(value) {
    const coerced = String(value ?? '').trim();
    if (this.#download === coerced) return;
    this.#download = coerced;
    if (coerced) {
      this.setAttribute('download', coerced);
    } else {
      this.removeAttribute('download');
    }
    this.requestUpdate();
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateAria();
  }

  #updateAria() {
    if (this.#disabled) {
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('tabindex', '-1');
    } else {
      this.removeAttribute('aria-disabled');
      this.setAttribute('tabindex', '0');
    }
  }

  #handleClick(event) {
    if (this.#disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (this.#href) {
      this.#emitNavigate();
    }
  }

  #handleKeydown(event) {
    if (this.#disabled) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.#href) {
        this.#emitNavigate();
      }
      this.click();
    }
  }

  #emitNavigate() {
    const effectiveRel = this.#target === '_blank' && !this.#rel
      ? 'noopener noreferrer'
      : this.#rel;

    const cancelled = !this.dispatchEvent(new CustomEvent('run-navigate', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        href: this.#href,
        target: this.#target,
        rel: effectiveRel,
        download: this.#download,
      },
    }));

    if (!cancelled && this.#href) {
      if (this.#download) {
        const a = document.createElement('a');
        a.href = this.#href;
        a.download = this.#download;
        if (effectiveRel) a.rel = effectiveRel;
        a.target = this.#target;
        a.click();
      } else if (this.#target === '_blank' || this.#target === '_parent' || this.#target === '_top') {
        window.open(this.#href, this.#target, effectiveRel ? `rel=${effectiveRel}` : '');
      } else {
        window.location.href = this.#href;
      }
    }
  }
}

if (!customElements.get('run-link')) {
  customElements.define('run-link', RunLink);
}

export { RunLink };
export default RunLink;